import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

import { fetchMemberPackage } from '@lib/members/data-access';
import { selectMemberPackage, selectMemberProfile } from '@lib/members/data-access';
import { checkClientsFreemium } from '@lib/members/data-access';
import { MemberHubConfig } from '@lib/members/feature/models/member-hub.interface';
import { HubRoute, HubRouteStatusTone } from '@lib/members/feature/models/navigation.interface';
import { HealthStatusCheckSchedulerComponent, MemberCommentsCardComponent, PackageCoverageButtonComponent } from '@lib/members/ui';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { tap } from 'rxjs';

import { MatTabLink, MatTabNav, MatTabNavPanel } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';

import { calculateAge } from '@lib/commons/util-functions/calculate-age.function';
import { findActiveContract } from '@lib/commons/util-functions/handle-contract.function';

import { MemberContract } from '@lib/core/models/contract.interface';
import { MemberProfile } from '@lib/core/models/member.interface';

import { ContractItemComponent } from '@lib/shared-ui/components/commons';
import { LoaderComponent } from '@lib/shared-ui/components/layout';

const STATUS_DOT_CLASSES: Record<HubRouteStatusTone, string> = {
  success: 'bg-success-500',
  warning: 'bg-warning-400',
  error: 'bg-error-500',
};

const DEFAULT_HUB_ROUTES: HubRoute[] = [
  { link: 'profile', label: 'context.tabs.profile' },
  { link: 'guarantees', label: 'context.tabs.guarantees' },
  { link: 'appointments', label: 'context.tabs.appointments' },
  { link: 'documents', label: 'context.tabs.documents' },
];

export { HubRoute };

@Component({
  selector: 'members-hub',
  standalone: true,
  imports: [
    MatTabNav,
    MatTabLink,
    MatTabNavPanel,
    MatTooltip,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    LoaderComponent,
    ContractItemComponent,
    MemberCommentsCardComponent,
    HealthStatusCheckSchedulerComponent,
    PackageCoverageButtonComponent,
  ],
  templateUrl: './member-hub.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberHubComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);

  readonly config = input<MemberHubConfig>({
    showComments: true,
    showHealthCheckScheduler: true,
    showPackageCoverage: false,
    enableFreemiumCheck: true,
  });
  readonly routes = input<HubRoute[]>();

  protected readonly hubRoutes = computed<HubRoute[]>(() => {
    if (this.routes()) return this.routes();

    return DEFAULT_HUB_ROUTES;
  });

  /**
   * Each tab as it renders, with whatever it currently has to report resolved here rather than in the
   * template: the status is a signal per tab, and reading it in one place is what keeps the bar
   * rebuilding when any of them moves.
   */
  protected readonly tabs = computed(() =>
    this.hubRoutes().map(route => {
      const status = route.status?.() ?? null;

      return {
        link: route.link,
        label: route.label,
        hintKey: status?.hintKey ?? '',
        dotClass: status ? `${STATUS_DOT_CLASSES[status.tone]}${status.pulse ? ' animate-pulse' : ''}` : '',
      };
    })
  );

  protected readonly calculateAge = calculateAge;
  protected readonly checkFreemium = checkClientsFreemium;

  private readonly memberPackage = this.store.selectSignal(selectMemberPackage);

  protected readonly memberProfile = signal<MemberProfile>(null);
  protected readonly loading = signal<boolean>(true);

  protected readonly lastContract = computed<MemberContract>(() => findActiveContract(this.memberProfile().contracts));

  ngOnInit(): void {
    this.getMemberProfile();
  }

  private getMemberProfile(): void {
    this.store
      .select(selectMemberProfile)
      .pipe(
        tap((memberProfile: MemberProfile) => {
          if (memberProfile && this.config().showPackageCoverage && this.memberPackage()?.userId !== memberProfile.userId) {
            this.store.dispatch(fetchMemberPackage({ userId: memberProfile.userId }));
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((memberProfile: MemberProfile) => {
        this.memberProfile.set(memberProfile);
        this.loading.set(false);
      });
  }
}
